import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Calendar, MapPin, Clock, Download, Loader2, Filter, ArrowUpDown, Eye, EyeOff, Upload, Image, X, Check } from 'lucide-react';

import { API } from '../../lib/api';

const CATEGORIES = [
  { value: 'musica', label: 'Musica' },
  { value: 'cultura', label: 'Cultura' },
  { value: 'gastronomia', label: 'Gastronomia' },
  { value: 'sport', label: 'Sport' },
  { value: 'festa', label: 'Festa' },
  { value: 'mercato', label: 'Mercato' },
  { value: 'altro', label: 'Altro' }
];

export default function AdminEvents() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Filtri e ordinamento
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = prossimi prima, 'desc' = più lontani prima
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    titolo: '',
    titolo_en: '',
    descrizione: '',
    descrizione_en: '',
    data: '',
    data_fine: '',
    ora: '',
    ora_fine: '',
    luogo: '',
    luogo_en: '',
    indirizzo: '',
    immagine_url: '',
    categoria: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      // Fetch all events including past ones for admin
      const response = await axios.get(`${API}/events?include_past=true`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPG, PNG, WEBP o GIF');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Immagine troppo grande. Max 5MB');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(`${API}/upload/event-image`, uploadFormData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });
      
      setFormData(prev => ({ ...prev, immagine_url: response.data.url }));
      toast.success('Immagine caricata!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Errore nel caricamento immagine');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        data_fine: formData.data_fine || formData.data // Se non specificata, usa data inizio
      };
      
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Evento aggiornato!');
      } else {
        await axios.post(`${API}/admin/events`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Evento creato!');
      }
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento eliminato!');
      fetchEvents();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      titolo: event.titolo,
      titolo_en: event.titolo_en || '',
      descrizione: event.descrizione,
      descrizione_en: event.descrizione_en || '',
      data: event.data,
      data_fine: event.data_fine || '',
      ora: event.ora || '',
      ora_fine: event.ora_fine || '',
      luogo: event.luogo,
      luogo_en: event.luogo_en || '',
      indirizzo: event.indirizzo || '',
      immagine_url: event.immagine_url || '',
      categoria: event.categoria || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      titolo: '',
      titolo_en: '',
      descrizione: '',
      descrizione_en: '',
      data: '',
      data_fine: '',
      ora: '',
      ora_fine: '',
      luogo: '',
      luogo_en: '',
      indirizzo: '',
      immagine_url: '',
      categoria: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const isEventPast = (event) => {
    const endDate = event.data_fine || event.data;
    return new Date(endDate) < new Date(new Date().toDateString());
  };

  // Eventi filtrati e ordinati
  const filteredEvents = useMemo(() => {
    let result = [...events];
    
    // Filtra eventi passati
    if (!showPastEvents) {
      result = result.filter(event => !isEventPast(event));
    }
    
    // Filtra per categoria
    if (categoryFilter !== 'all') {
      result = result.filter(event => event.categoria === categoryFilter);
    }
    
    // Ordina per data
    result.sort((a, b) => {
      const dateA = new Date(a.data);
      const dateB = new Date(b.data);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    
    return result;
  }, [events, showPastEvents, categoryFilter, sortOrder]);

  // Conta eventi passati per mostrare nel badge
  const pastEventsCount = useMemo(() => {
    return events.filter(event => isEventPast(event)).length;
  }, [events]);

  const handleScrapeSagre = async () => {
    if (!window.confirm('Vuoi importare gli eventi da Sagre.it (Provincia di Salerno)? Gli eventi duplicati saranno aggiornati.')) return;
    
    setScraping(true);
    try {
      const response = await axios.post(`${API}/admin/scrape-events-sagre`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { eventi_trovati, eventi_importati, eventi_aggiornati } = response.data;
      
      if (eventi_importati > 0 || eventi_aggiornati > 0) {
        toast.success(`Importati ${eventi_importati} nuovi eventi, ${eventi_aggiornati} aggiornati!`);
        fetchEvents();
      } else {
        toast.info(response.data.message || 'Nessun evento trovato da importare.');
      }
    } catch (error) {
      console.error('Scraping sagre error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione da Sagre.it');
    } finally {
      setScraping(false);
    }
  };

  const handleScrapeMuseo = async () => {
    if (!window.confirm('Vuoi importare gli eventi dal Parco Archeologico di Paestum? Gli eventi duplicati saranno aggiornati.')) return;
    
    setScraping(true);
    try {
      const response = await axios.post(`${API}/admin/scrape-events-museo`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { eventi_trovati, eventi_importati, eventi_aggiornati } = response.data;
      
      if (eventi_importati > 0 || eventi_aggiornati > 0) {
        toast.success(`Importati ${eventi_importati} nuovi eventi, ${eventi_aggiornati} aggiornati!`);
        fetchEvents();
      } else {
        toast.info(response.data.message || 'Nessun evento trovato da importare.');
      }
    } catch (error) {
      console.error('Scraping museo error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione dal museo');
    } finally {
      setScraping(false);
    }
  };

  return (
    <AdminLayout title="Gestione Eventi">
      {/* Header con statistiche */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <p className="font-manrope text-[#4A5568]">
            {filteredEvents.length} eventi {!showPastEvents ? 'futuri' : 'totali'}
            {categoryFilter !== 'all' && ` (${CATEGORIES.find(c => c.value === categoryFilter)?.label})`}
          </p>
          {!showPastEvents && pastEventsCount > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              {pastEventsCount} eventi passati nascosti
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={handleScrapeSagre}
            disabled={scraping}
            variant="outline"
            className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10"
            data-testid="scrape-sagre-btn"
          >
            {scraping ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Importa da Sagre.it
              </>
            )}
          </Button>
          <Button 
            onClick={handleScrapeMuseo}
            disabled={scraping}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
            data-testid="scrape-museo-btn"
          >
            {scraping ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Importa da Museo Paestum
              </>
            )}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white" data-testid="new-event-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Evento
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-cinzel">
                {editingEvent ? 'Modifica Evento' : 'Nuovo Evento'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Titolo *</Label>
                <Input
                  value={formData.titolo}
                  onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                  required
                  placeholder="Nome dell'evento"
                />
              </div>
              <div>
                <Label>Titolo (Inglese) 🇬🇧</Label>
                <Input
                  value={formData.titolo_en}
                  onChange={(e) => setFormData({...formData, titolo_en: e.target.value})}
                  placeholder="Event name in English"
                />
              </div>
              
              <div>
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.descrizione}
                  onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                  rows={3}
                />
              </div>
              <div>
                <Label>Descrizione (Inglese) 🇬🇧</Label>
                <Textarea
                  value={formData.descrizione_en}
                  onChange={(e) => setFormData({...formData, descrizione_en: e.target.value})}
                  rows={3}
                  placeholder="Description in English"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data Inizio *</Label>
                  <Input
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({...formData, data: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Data Fine</Label>
                  <Input
                    type="date"
                    value={formData.data_fine}
                    onChange={(e) => setFormData({...formData, data_fine: e.target.value})}
                    min={formData.data}
                  />
                  <p className="text-xs text-gray-500 mt-1">Lascia vuoto per eventi di 1 giorno</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ora Inizio</Label>
                  <Input
                    type="time"
                    value={formData.ora}
                    onChange={(e) => setFormData({...formData, ora: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Ora Fine</Label>
                  <Input
                    type="time"
                    value={formData.ora_fine}
                    onChange={(e) => setFormData({...formData, ora_fine: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Luogo *</Label>
                <Input
                  value={formData.luogo}
                  onChange={(e) => setFormData({...formData, luogo: e.target.value})}
                  required
                  placeholder="Es. Piazza del Tempio"
                />
              </div>
              <div>
                <Label>Luogo (Inglese) 🇬🇧</Label>
                <Input
                  value={formData.luogo_en}
                  onChange={(e) => setFormData({...formData, luogo_en: e.target.value})}
                  placeholder="E.g. Temple Square"
                />
              </div>

              <div>
                <Label>Indirizzo</Label>
                <Input
                  value={formData.indirizzo}
                  onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                  placeholder="Via Roma 1, Paestum"
                />
              </div>

              <div>
                <Label>Categoria</Label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                >
                  <option value="">Seleziona...</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Immagine Evento</Label>
                <div className="mt-2 space-y-3">
                  {/* Preview immagine se presente */}
                  {formData.immagine_url && (
                    <div className="relative inline-block">
                      <img 
                        src={formData.immagine_url.startsWith('http') ? formData.immagine_url : `${process.env.REACT_APP_BACKEND_URL}${formData.immagine_url}`} 
                        alt="Anteprima" 
                        className="h-32 w-auto rounded-lg border object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, immagine_url: ''})}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload area */}
                  {!formData.immagine_url && (
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      uploadingImage ? 'border-[#C5A059] bg-amber-50' : 'border-gray-300 hover:border-[#C5A059]'
                    }`}>
                      {uploadingImage ? (
                        <div className="flex flex-col items-center">
                          <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin mb-2" />
                          <p className="text-sm text-gray-500">Caricamento in corso...</p>
                        </div>
                      ) : (
                        <label className="cursor-pointer flex flex-col items-center">
                          <Image className="w-10 h-10 text-gray-400 mb-2" />
                          <span className="text-sm font-medium text-gray-700">Clicca per caricare</span>
                          <span className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP, GIF (max 5MB)</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                          />
                        </label>
                      )}
                    </div>
                  )}
                  
                  {/* Opzione URL manuale */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">oppure inserisci URL:</span>
                    <Input
                      value={formData.immagine_url}
                      onChange={(e) => setFormData({...formData, immagine_url: e.target.value})}
                      placeholder="https://..."
                      className="text-xs h-8"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#C5A059] hover:bg-[#B08D45]">
                {editingEvent ? 'Salva Modifiche' : 'Crea Evento'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Barra filtri */}
      <div className="flex flex-wrap items-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg">
        {/* Toggle eventi passati */}
        <Button
          variant={showPastEvents ? "default" : "outline"}
          size="sm"
          onClick={() => setShowPastEvents(!showPastEvents)}
          className={showPastEvents ? "bg-gray-600 hover:bg-gray-700" : "border-gray-300"}
          data-testid="toggle-past-events"
        >
          {showPastEvents ? (
            <>
              <Eye className="w-4 h-4 mr-2" />
              Passati visibili
            </>
          ) : (
            <>
              <EyeOff className="w-4 h-4 mr-2" />
              Passati nascosti
            </>
          )}
          {pastEventsCount > 0 && (
            <span className="ml-2 bg-white/20 px-1.5 py-0.5 rounded text-xs">
              {pastEventsCount}
            </span>
          )}
        </Button>

        {/* Ordinamento data */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="border-gray-300"
          data-testid="sort-events"
        >
          <ArrowUpDown className="w-4 h-4 mr-2" />
          {sortOrder === 'asc' ? 'Prossimi prima' : 'Più lontani prima'}
        </Button>

        {/* Filtro categoria */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
            data-testid="category-filter"
          >
            <option value="all">Tutte le categorie</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      ) : filteredEvents.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
            <p className="font-manrope text-[#4A5568]">
              {events.length === 0 
                ? "Nessun evento. Clicca \"Nuovo Evento\" per iniziare."
                : "Nessun evento corrisponde ai filtri selezionati."}
            </p>
            {events.length > 0 && !showPastEvents && (
              <Button 
                variant="link" 
                onClick={() => setShowPastEvents(true)}
                className="mt-2 text-[#C5A059]"
              >
                Mostra eventi passati ({pastEventsCount})
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredEvents.map((event) => {
            const isPast = isEventPast(event);
            return (
              <Card key={event.id} className={`border-[#E2E8F0] ${isPast ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {isPast && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                            Passato
                          </span>
                        )}
                        {event.categoria && (
                          <span className="text-xs bg-[#C5A059]/10 text-[#C5A059] px-2 py-0.5 rounded uppercase">
                            {CATEGORIES.find(c => c.value === event.categoria)?.label || event.categoria}
                          </span>
                        )}
                      </div>
                      <h3 className="font-cinzel text-lg text-[#1A202C] mb-1">{event.titolo}</h3>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-[#4A5568]">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-[#C5A059]" />
                          {formatDate(event.data)}
                          {event.data_fine && event.data_fine !== event.data && (
                            <> - {formatDate(event.data_fine)}</>
                          )}
                        </span>
                        {event.ora && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-[#C5A059]" />
                            {event.ora}
                            {event.ora_fine && <> - {event.ora_fine}</>}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-[#C5A059]" />
                          {event.luogo}
                        </span>
                      </div>
                      
                      {event.descrizione && (
                        <p className="text-sm text-[#718096] mt-2 line-clamp-2">{event.descrizione}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      {/* Thumbnail immagine evento */}
                      {event.immagine_url && (
                        <img 
                          src={event.immagine_url.startsWith('http') ? event.immagine_url : `${process.env.REACT_APP_BACKEND_URL}${event.immagine_url}`}
                          alt={event.titolo}
                          className="w-16 h-16 rounded-lg object-cover border"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => openEditDialog(event)}
                        className="border-[#C5A059] text-[#C5A059]"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDelete(event.id)}
                        className="border-red-400 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
